import { useEffect, useState } from "react";
import { adminApi } from "@/services/api/admin";
import { ApiErrorResponse } from "@/services/api/client";
import { teacherApi } from "@/services/api/teacher";
import { useTalkT } from "./useTalkT";
import { talkRadarStageStyle } from "./talkRadarStage";

type LessonPick = {
  id: string;
  date: string | null;
  time_slot_name: string | null;
};

function lessonPicks(
  lessons: { id?: string | null; date?: string | null; time_slot_name?: string | null }[]
): LessonPick[] {
  return lessons.flatMap((lesson) =>
    lesson.id
      ? [
          {
            id: lesson.id,
            date: lesson.date ?? null,
            time_slot_name: lesson.time_slot_name ?? null,
          },
        ]
      : []
  );
}

function lessonLabel(lesson: LessonPick): string {
  const date = lesson.date
    ? new Date(lesson.date).toLocaleDateString("de-CH")
    : "—";
  return lesson.time_slot_name ? `${date}, ${lesson.time_slot_name}` : date;
}

function sendError(err: unknown, fallback: string): string {
  if (err instanceof ApiErrorResponse && err.detail) return err.detail;
  return fallback;
}

export function TalkRadarMessage({
  requestId,
  teachersDeclined,
  teachersPending,
  onSent,
}: {
  requestId: string;
  teachersDeclined?: number;
  teachersPending?: number;
  onSent: () => void;
}) {
  const t = useTalkT();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [lessons, setLessons] = useState<LessonPick[]>([]);
  const [selectedLessonIds, setSelectedLessonIds] = useState<string[]>([]);
  const [selectAllLessons, setSelectAllLessons] = useState(true);
  const [includeDeclined, setIncludeDeclined] = useState(true);
  const [includePending, setIncludePending] = useState(false);
  const declinedCount = teachersDeclined ?? 0;
  const pendingCount = teachersPending ?? 0;

  useEffect(() => {
    let live = true;
    adminApi
      .getSubstitutionDetail(requestId)
      .then((detail) => {
        if (!live) return;
        const next = lessonPicks(detail.lessons || []);
        setLessons(next);
        setSelectedLessonIds(next.map((lesson) => lesson.id));
        setSelectAllLessons(true);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [requestId]);

  function toggleLesson(lessonId: string) {
    setSelectedLessonIds((prev) =>
      prev.includes(lessonId)
        ? prev.filter((id) => id !== lessonId)
        : [...prev, lessonId]
    );
    setSelectAllLessons(false);
  }

  function toggleAll(checked: boolean) {
    setSelectAllLessons(checked);
    setSelectedLessonIds(checked ? lessons.map((lesson) => lesson.id) : []);
  }

  async function lessonIdsForSend(): Promise<string[]> {
    if (lessons.length > 0) return selectedLessonIds;
    const detail = await adminApi.getSubstitutionDetail(requestId);
    return lessonPicks(detail.lessons || []).map((lesson) => lesson.id);
  }

  async function recipientIds(lessonIds: string[]): Promise<string[]> {
    const ids: string[] = [];
    if (includeDeclined && (teachersDeclined == null || teachersDeclined > 0)) {
      const declined = await teacherApi.getLessonTeachers(lessonIds, "declined");
      ids.push(...(declined.teacher_ids || []));
    }
    if (includePending && (teachersPending == null || teachersPending > 0)) {
      const pending = await teacherApi.getLessonTeachers(lessonIds, "pending");
      ids.push(...(pending.teacher_ids || []));
    }
    return ids;
  }

  async function send() {
    const message = text.trim();
    if (!message || sending) return;
    setSending(true);
    setError(null);
    try {
      const lessonIds = await lessonIdsForSend();
      if (lessonIds.length === 0) {
        setError(t("talk_radar_no_lessons"));
        return;
      }
      const recipients = await recipientIds(lessonIds);
      if (recipients.length === 0) {
        setError(t("talk_radar_no_recipients"));
        return;
      }
      await teacherApi.sendRadarMessage(lessonIds, {
        recipient_teacher_ids: recipients,
        message_text: message,
        include_lesson_details: true,
      });
      onSent();
    } catch (err: unknown) {
      setError(sendError(err, t("talk_radar_message_failed")));
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ ...talkRadarStageStyle, zIndex: 4 }}>
      <h2>{t("talk_radar_message")}</h2>
      {lessons.length > 0 && (
        <div>
          <h3>{t("talk_radar_lessons")}</h3>
          <label>
            <input
              type="checkbox"
              checked={selectAllLessons}
              onChange={(event) => toggleAll(event.target.checked)}
            />
            {t("talk_radar_all_lessons", { count: lessons.length })}
          </label>
          {!selectAllLessons &&
            lessons.map((lesson) => (
              <label key={lesson.id}>
                <input
                  type="checkbox"
                  checked={selectedLessonIds.includes(lesson.id)}
                  onChange={() => toggleLesson(lesson.id)}
                />
                {lessonLabel(lesson)}
              </label>
            ))}
        </div>
      )}
      <div>
        <h3>{t("talk_radar_recipients")}</h3>
        <label>
          <input
            type="checkbox"
            checked={includeDeclined}
            disabled={teachersDeclined === 0}
            onChange={(event) => setIncludeDeclined(event.target.checked)}
          />
          {t("talk_radar_declined", { count: declinedCount })}
        </label>
        <label>
          <input
            type="checkbox"
            checked={includePending}
            disabled={teachersPending === 0}
            onChange={(event) => setIncludePending(event.target.checked)}
          />
          {t("talk_radar_pending", { count: pendingCount })}
        </label>
      </div>
      <textarea
        aria-label={t("talk_radar_message")}
        rows={5}
        value={text}
        onChange={(event) => setText(event.target.value)}
        style={{
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          marginTop: 12,
          color: "#e2e8f0",
          background: "transparent",
          border: "1px solid rgba(148, 163, 184, 0.45)",
          borderRadius: 12,
          padding: 12,
        }}
      />
      {error && <p>{error}</p>}
      <button
        type="button"
        onClick={() => void send()}
        disabled={sending}
        style={{
          marginTop: 12,
          color: "#e2e8f0",
          background: "transparent",
          border: "1px solid rgba(148, 163, 184, 0.45)",
          borderRadius: 12,
          padding: "10px 14px",
        }}
      >
        {t("talk_radar_send")}
      </button>
    </div>
  );
}
